import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState
} from 'react'
import { setupAxios } from '../service/api-interseptor/axiosInstance'
import axios from 'axios'
import type { RequestData, SettingModel, UserModel } from '@velocity/shared'
import { useQuery } from '@tanstack/react-query'
import { get_request_api, refresh_token_api } from '@velocity/shared'

type AuthContextProps = {
  token: string | null
  currentUser: UserModel
  login: (token: string, refresh_token: string) => void
  logout: () => void
  refresh: () => void
}

const initialState = {
  token: localStorage.getItem('accessToken'),
  currentUser:
    localStorage.getItem('user') ?
      JSON.parse(localStorage.getItem('user')!)
    : null,
  login: (token: string, refresh_token: string) => {},
  logout: () => {},
  refresh: () => {}
}

const AuthContext = createContext<AuthContextProps>(initialState)

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState(initialState)
  const { data } = useQuery({
    queryKey: ['profile'],
    queryFn: get_request_api,
    enabled: !!state.token,
    initialData: state.currentUser
  })
  useEffect(() => {
    if (data) {
      setState((prev) => ({ ...prev, currentUser: data }))
      localStorage.setItem('user', JSON.stringify(data))
    }
  }, [data])
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: get_request_api as (data: RequestData) => Promise<SettingModel>,
    enabled: !!state.token
  })
  useEffect(() => {
    if (settings) {
      localStorage.setItem('settings', JSON.stringify(settings))
    }
  }, [settings])
  const login = async (token: string, refresh_token: string) => {
    localStorage.setItem('accessToken', token)
    setState({ ...state, token })
    localStorage.setItem('refreshToken', refresh_token)
  }
  const logout = useCallback(() => {
    setState({ ...initialState, token: null, currentUser: null })
    localStorage.clear()
  }, [])
  const refresh = useCallback(async () => {
    const refresh_token = localStorage.getItem('refreshToken')
    if (refresh_token) {
      try {
        const auth = await refresh_token_api(refresh_token)
        if (auth) login(auth.token, auth.refresh_token)
        else logout()
      } catch (error) {
        console.error('Error refreshing token', error)
        logout()
      }
    }
    // eslint-disable-next-line
  }, [])
  useLayoutEffect(() => {
    setupAxios(axios, logout, refresh)
    // eslint-disable-next-line
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  return useContext(AuthContext)
}

export { AuthProvider, useAuth }
